//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { useCallback, useEffect, useRef } from 'react';

/**
 * 通用計時器 Hook
 * @param options 配置選項
 * @returns 計時器控制方法
 */
interface UseTimerOptions<T = any> {
  /** 檢查間隔時間 (毫秒) */
  checkInterval: number;
  /** 超時時間 (毫秒) */
  timeoutDuration: number;
  /** 超時模式：'progress'：只要有進度就重置超時 | 'fixed'：固定時長 */
  timeoutMode?: 'progress' | 'fixed';
  /** 檢查進度的函數 */
  onCheck: () => Promise<T>;
  /** 判斷是否應該繼續計時 */
  shouldContinue: (data: T) => boolean;
  /** 判斷是否成功完成 */
  isSuccess: (data: T) => boolean;
  /** 判斷是否失敗 */
  isError: (data: T) => boolean;
  /** 成功時的回調 */
  onSuccess?: (data: T) => void;
  /** 失敗時的回調 */
  onError?: (data: T) => void;
  /** 超時時的回調 */
  onTimeout?: () => void;
  /** 進度更新時的回調 */
  onProgress?: (data: T) => void;
  /** 檢查數據是否有更新 */
  hasUpdated?: (newData: T, oldData: T) => boolean;
}

export const useTimer = <T = any,>(options: UseTimerOptions<T>) => {
  const {
    checkInterval,
    timeoutDuration,
    timeoutMode = 'progress', // 預設為進度型
    onCheck,
    shouldContinue,
    isSuccess,
    isError,
    onSuccess,
    onError,
    onTimeout,
    onProgress,
    hasUpdated,
  } = options;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef(false);
  const dataRef = useRef<T | null>(null);
  const startTimeRef = useRef<number>(0); // 記錄開始時間

  // 停止所有計時器
  const stopAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isRunningRef.current = false;
  }, []);

  // 重置超時計時器（僅在 progress 模式下使用）
  const resetTimeoutTimer = useCallback(() => {
    if (timeoutMode === 'fixed') {
      // 固定時長模式不重置超時計時器
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      console.log('Timer timeout - no updates detected');
      stopAllTimers();
      onTimeout?.();
    }, timeoutDuration);
  }, [timeoutMode, timeoutDuration, onTimeout, stopAllTimers]);

  // 檢查是否已經超過固定時長
  const checkFixedTimeout = useCallback(() => {
    if (timeoutMode === 'fixed') {
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed >= timeoutDuration) {
        console.log('Timer timeout - fixed duration exceeded');
        stopAllTimers();
        onTimeout?.();
        return true;
      }
    }
    return false;
  }, [timeoutMode, timeoutDuration, onTimeout, stopAllTimers]);

  // 檢查進度
  const checkProgress = useCallback(async () => {
    // 固定時長模式：先檢查是否超時
    if (checkFixedTimeout()) {
      return;
    }

    try {
      const newData = await onCheck();

      // 檢查是否成功
      if (isSuccess(newData)) {
        stopAllTimers();
        onSuccess?.(newData);
        return;
      }

      // 檢查是否失敗
      if (isError(newData)) {
        stopAllTimers();
        onError?.(newData);
        return;
      }

      // 檢查是否應該繼續
      if (!shouldContinue(newData)) {
        stopAllTimers();
        return;
      }

      // 檢查數據是否有更新
      const updated = hasUpdated ? hasUpdated(newData, dataRef.current as T) : true;

      if (updated) {
        dataRef.current = newData;
        onProgress?.(newData);

        // 只在進度型模式下重置超時計時器
        if (timeoutMode === 'progress') {
          resetTimeoutTimer();
        }
      }
    } catch (error) {
      console.error('Error during timer check:', error);
    }
  }, [
    checkFixedTimeout,
    onCheck,
    shouldContinue,
    isSuccess,
    isError,
    onSuccess,
    onError,
    onProgress,
    hasUpdated,
    timeoutMode,
    resetTimeoutTimer,
    stopAllTimers,
  ]);

  // 開始計時
  const startTimer = useCallback(
    (initialData?: T) => {
      if (isRunningRef.current) {
        console.warn('Timer is already running');
        return;
      }

      isRunningRef.current = true;
      dataRef.current = initialData || null;
      startTimeRef.current = Date.now(); // 記錄開始時間

      // 初始化時顯示進度
      if (initialData) {
        onProgress?.(initialData);
      }

      // 啟動超時計時器（兩種模式都需要）
      if (timeoutMode === 'progress') {
        resetTimeoutTimer();
      } else {
        // 固定時長模式：設定一次性超時
        timeoutRef.current = setTimeout(() => {
          console.log('Timer timeout - fixed duration exceeded');
          stopAllTimers();
          onTimeout?.();
        }, timeoutDuration);
      }

      // 立即執行一次檢查
      checkProgress();

      // 設置定期檢查
      intervalRef.current = setInterval(checkProgress, checkInterval);
    },
    [checkInterval, checkProgress, resetTimeoutTimer, onProgress, timeoutMode, timeoutDuration, onTimeout, stopAllTimers]
  );

  // 停止計時
  const stopTimer = useCallback(() => {
    stopAllTimers();
  }, [stopAllTimers]);

  // 組件卸載時清理
  useEffect(() => {
    return () => {
      stopAllTimers();
    };
  }, [stopAllTimers]);

  return {
    startTimer,
    stopTimer,
    isRunning: isRunningRef.current,
    currentData: dataRef.current,
  };
};
